import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { QrCode, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    api.get(`/auth/verify-reset-token/${token}`)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Token may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in">
        <Card className="border-border">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="flex items-center justify-center gap-2 mb-6">
              <QrCode className="text-primary" size={28} />
              <span className="font-heading text-xl font-bold">Smart-QR</span>
            </Link>

            {success ? (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="text-emerald-500" size={28} />
                  </div>
                </div>
                <CardTitle className="font-heading text-2xl">Password Reset</CardTitle>
                <CardDescription className="mt-2">
                  Your password has been reset successfully. You can now sign in with your new password.
                </CardDescription>
              </>
            ) : !token || !tokenValid ? (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                    <XCircle className="text-destructive" size={28} />
                  </div>
                </div>
                <CardTitle className="font-heading text-2xl">Invalid or Expired Link</CardTitle>
                <CardDescription className="mt-2">
                  This password reset link is invalid or has expired. Please request a new one.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="font-heading text-2xl">Set new password</CardTitle>
                <CardDescription>Choose a strong password for your account</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {success ? (
              <Link to="/login">
                <Button className="w-full btn-glow" data-testid="reset-go-login-btn">
                  Sign In
                </Button>
              </Link>
            ) : !token || !tokenValid ? (
              <Link to="/forgot-password">
                <Button className="w-full btn-glow" data-testid="reset-request-new-btn">
                  Request New Link
                </Button>
              </Link>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      id="password"
                      type="password"
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      id="confirm"
                      type="password"
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-glow" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
